// src/pages/CheckEmailFuiteDonneesPage.tsx

import React, { useContext, useEffect, useState } from "react";
import './style.scss';
import RegistrationHeader from "./components/RegistrationHeader";

import { ReactComponent as TopRightSVG } from 'assets/img/particules_top-right.svg';
import { ReactComponent as BottomLeftSVG } from 'assets/img/particules_bottom-left.svg';
import { decryptApiBodyToStringWordPress } from "shared/api/cryptApiBody";
import { checkEmail, tempRegister } from "features/account";
import { extractEmailParam } from "shared/lib/email_utils";
import Button from "shared/ui/Button";
import { ReactComponent as IconNext } from "assets/img/icons/icon_arrow-Right.svg";
import { ReactComponent as IconNotif } from "assets/img/icons/icon_bell.svg";
import Timeline from "components/Timeline/Timeline";
import { BreachesContext } from "features/breaches";
import { useLocation, useNavigate } from "react-router-dom";
import { localStorageUtil } from "shared/lib/localStorage_util";
import { UserContext } from "features/account";
import PageHeader from "components/PageHeader";

// Timeline Loader Component
const TimelineLoader = () => (
  <div className="timeline-loader">
    <div className="timeline-loader-spinner"></div>
    <p className="timeline-loader-text">Recherche de fuites de données...</p>
  </div>
);

const CheckEmailFuitesDonneesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);


  const { breaches, getBreachesByEmail, loading } = useContext(BreachesContext);
  const [nextPageUrl, setNextPageUrl] = useState(`/fuites-donnees/active-id-tracker/new?email=${extractEmailParam(window.location.href)}`);

  useEffect(() => {
    const fullUrl = window.location.href;
    const encodedEmail = extractEmailParam(fullUrl);

    if (encodedEmail) {
      const decodedEmail = decodeURIComponent(encodedEmail);
      const email = decryptApiBodyToStringWordPress(decodedEmail);

      getBreachesByEmail(email ?? "");
    }
  }, [getBreachesByEmail, location.pathname]);

  // Initialize session on mount
  useEffect(() => {
    const fullUrl = window.location.href;
    const encodedEmail = extractEmailParam(fullUrl);

    const initializeSession = async () => {
      try {
        if (!encodedEmail) {
          console.warn('Email non trouvé');
          return;
        }
        const decodedEmail = decodeURIComponent(encodedEmail);
        const email = decryptApiBodyToStringWordPress(decodedEmail);

        // First call to server to generate session token
        const response = await tempRegister({email, isNewUserFromWordpress: true, type: 'individual'});

        if (response?.sessionPageToken) {
          localStorageUtil.set(localStorageUtil.KEYS.REGISTRATION_SESSION, response.sessionPageToken);
        }
      } catch (error) {
        console.error("Failed to initialize session:", error);
        const fallbackToken = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorageUtil.set(localStorageUtil.KEYS.REGISTRATION_SESSION, fallbackToken);
      } finally {
      }
    };

    const savedToken  = localStorageUtil.get(localStorageUtil.KEYS.REGISTRATION_SESSION);

    if (savedToken) {
      //
    } else {
      initializeSession();
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  const getBreachesText = (count: number | undefined) => {
    if (!count || count === 0) {
      return {
        number: 0,
        text: "fuite de données détectée"
      };
    }

    const number = count === 1 ? 1 : count;
    const fuiteWord = count === 1 ? "fuite" : "fuites";

    return {
      number: number,
      text: `${fuiteWord} de données détectées`
    };
  };

  useEffect(() => {
    const fullUrl = window.location.href;
    const encodedEmail = extractEmailParam(fullUrl);

    if (encodedEmail) {
      const decodedEmail = decodeURIComponent(encodedEmail);
      const email = decryptApiBodyToStringWordPress(decodedEmail);
      checkEmailExistance(email)
    }
  }, []);

  const checkEmailExistance = async (email?: string, phoneNumber?: string) => {
    if (email) {
      if (user?.email) {
        setNextPageUrl(`/particuliers/id-tracker-pricing`);
      } else {
        const response = await checkEmail(email);
        if (response?.success !== undefined) { // Si email n'est pas utilisé
          setNextPageUrl(`/fuites-donnees/active-id-tracker/new?email=${extractEmailParam(window.location.href)}`);
        } else {
          setNextPageUrl(`/fuites-donnees/pricing`);
        }
      }
    }
  };

  const breachesText = getBreachesText(breaches?.length);

  return (
    <div className="registration-page">
      {/* Top right SVG background */}
      <div className="svg-background svg-background--top-right">
        <TopRightSVG />
      </div>

      {/* Bottom left SVG background */}
      <div className="svg-background svg-background--bottom-left">
        <BottomLeftSVG />
      </div>

      {!user?.email ? (
        <RegistrationHeader />
      ): (
        <PageHeader
          title={"ID TRACKER"}
          description={"fuites de données"}
          notification={user?.type === 'individual'}
          protectAlert={user?.type === 'individual'}
        >
        </PageHeader>
      )}

      <div className="registration-container-large">
        <div className="registration-content">
          <div className={"d-flex flex-column gap-1 p-2"}>
            <div className={"text-uppercase bold text-3xl"}>
              {loading ? (
                <span className="loading-counter">
                  <span className="counter-spinner"></span>
                  Analyse en cours...
                </span>
              ) : (
                <>
                  <span className={`text-warning ${breachesText.number >= 10 ? 'px-3 py-2' : 'px-6 py-1'} text-5xl bg-primary-70 rounded-full`}>{breachesText.number}</span>
                  <span className="text-primary"> {breachesText.text}</span>
                </>
              )}
            </div>
            {loading ? (
              <span className="loading-counter">
                Veuillez patienter...
              </span>
            ) : (
              <div className={"text-justify"}>
                {breaches?.length > 0 ? (
                  <>
                    Nous avons détecté votre adresse email dans une base de données mondiale. Les fuites listées ci-dessous
                    contiennent des informations vous concernant qui ont été exposées publiquement suite à des violations
                    de sécurité.
                  </>
                ) : (
                  <>
                    Nous n'avons pas détecté votre adresse email dans une base de données mondiale, elle n'a pas été exposée
                    publiquement suite à des violations de sécurité.
                  </>
                )}
              </div>
            )}

            <div
              className={"d-flex flex-wrap flex-row justify-content-between align-items-center gap-05 bg-primary p-4 rounded-lg text-light"}>
              <div className={"d-flex flex-column gap-05 w-2/3"}>
                <span className={"bold text-mm"}>Restez protégé</span>
                <span className={"text-neutral-50 text-sm"}>Soyez averti(e) lorsque votre identité apparaît dans de futures fuites de données.</span>
              </div>
              <div>
                <Button
                  icon={<IconNext width={16} height={16} />}
                  iconColor={"#171C60"}
                  iconLeft={<IconNotif width={16} height={16} />}
                  onClick={() => {
                    navigate(`${nextPageUrl}`);
                  }}
                  overrideClassName={"hover:shadow bg-warning bold shadow-md text-secondary rounded-lg border border-light text-sm px-4 py-3 d-flex gap-05 justify-center align-items-center"}
                  label={"Activer la surveillance"}
                />
              </div>
            </div>
          </div>
          <div className="animate-fade-in mt-2 px-1">
            {loading ? <TimelineLoader /> : <Timeline breaches={breaches} showDescription={false} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckEmailFuitesDonneesPage;